import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useCallback, useMemo } from 'react';
import { ContentLayout, StepperCard } from '@ibk/shared-layout';
import { useTranslation } from '@ibk/shared-translation';
import { AddressCard } from '../../../../components/AddressCard';
import { useEmissorContractAddresses, useEmissorUpdateContractAddress } from '../../../../hooks';
import { isValidAddress } from '../../../../utils/addressFormatter';
import { NewRechargeStep, NewRechargeStepStatus } from '../../types';
import styles from './EmissorDeliveryAddress.module.css';
import { mapAddressToUpdateContractAddressBody, mapEmissorContractAddressItemToAddress, } from './emissorDeliveryAddressMapper';
export function EmissorDeliveryAddress({ contractId, formData, onComplete, onReturn, onSaveAddress, onSaveSuccess, onSaveError, isLoading = false, headerStepper, }) {
    const { t } = useTranslation();
    const { primaryAddress, isLoading: isAddressesLoading } = useEmissorContractAddresses({ contractId });
    const { updateAddress, isUpdating } = useEmissorUpdateContractAddress();
    const defaultAddress = useMemo(() => mapEmissorContractAddressItemToAddress(primaryAddress), [primaryAddress]);
    const selectedAddress = formData.emissorDeliveryAddress ?? defaultAddress;
    const hasAddress = selectedAddress ? isValidAddress(selectedAddress) : false;
    const breadcrumbs = useMemo(() => [
        { label: t('recharge.breadcrumb.benefits'), href: '/beneficios' },
        { label: t('recharge.breadcrumb.new-recharge'), isActive: true },
    ], [t]);
    const currentStepConfig = useMemo(() => ({
        id: NewRechargeStep.DELIVERY_ADDRESS,
        title: t('recharge.step.delivery-address'),
        number: 5,
        status: NewRechargeStepStatus.ACTIVE,
    }), [t]);
    const handleSaveAddress = useCallback(async (address) => {
        const nextAddress = address;
        if (contractId && primaryAddress?.id) {
            const updated = await updateAddress({
                contractId,
                addressId: primaryAddress.id,
                body: mapAddressToUpdateContractAddressBody(nextAddress),
            });
            onSaveAddress(mapEmissorContractAddressItemToAddress(updated) ?? nextAddress);
        }
        else {
            onSaveAddress(nextAddress);
        }
    }, [contractId, primaryAddress, updateAddress, onSaveAddress]);
    return (_jsx(ContentLayout, { breadcrumbs: breadcrumbs, showSidebar: false, noMaxWidth: true, "data-testid": 'viewEmissorDeliveryAddressContainer', children: _jsx("div", { className: styles.wizardCardShell, children: _jsxs(StepperCard, { currentStep: currentStepConfig, showStepper: !headerStepper, actions: {
                    back: {
                        label: t('recharge.button.back'),
                        onClick: onReturn,
                    },
                    next: {
                        label: t('recharge.button.next'),
                        onClick: onComplete,
                        disabled: !hasAddress || isLoading || isAddressesLoading || isUpdating,
                    },
                }, children: [headerStepper, _jsxs("div", { className: styles.stepContent, "data-testid": 'viewEmissorDeliveryAddressStepContent', children: [_jsx("p", { className: styles.description, "data-testid": 'textEmissorDeliveryAddressDescription', children: t('recharge.deliveryAddress.description') }), _jsx(AddressCard, { loading: isAddressesLoading, isSaving: isUpdating, title: t('deliveryAddress'), modalTitle: t('deliveryAddress'), showEditButton: true, showValidIcon: true, location: selectedAddress, onSave: handleSaveAddress, onSaveSuccess: onSaveSuccess, onSaveError: onSaveError, testIdIdentifier: 'EmissorDeliveryAddressAddressCard' })] })] }) }) }));
}
